import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/en";
import { formatter } from "../lib/utils";
import TokenService from "../services/tokenService";

export const API_BASE_URL = window.GLOBAL_API_BASE_URL; // Update with your backend API URL

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    // Add Authorization header
    const token = TokenService.getAuthToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    // Add X-TenantId header
    const tenantId = window.TENANT_ID || 
                     sessionStorage.getItem('X-TenantId') || 
                     localStorage.getItem('X-TenantId');
    if (tenantId) {
      config.headers["X-TenantId"] = tenantId.toString();
    }

    // Debug log
    console.log('Request Headers:', config.headers);
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.log('Unauthorized access, redirecting to login');
      TokenService.clearAll();
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Add a function to set the JWT token to the headers
const setAuthToken = (token, tenantId, tenantName) => {
  if (token) {
    TokenService.setAuthToken(token);
    TokenService.setTenantId(tenantId);
    TokenService.setTenantName(tenantName);
  } else {
    TokenService.clearAll();
  }
};

const login = async (username, password) => {
  try {
    const response = await axiosInstance.post("/login", {
      username,
      password,
    });

    const { isSuccess, result, message } = response.data;

    if (isSuccess) {
      const { user, token } = result;
      // This will now set the token in sessionStorage and set up the axios instance
      setAuthToken(token, user.tenantId, user.tenantName);
      return user;
    } else {
      console.error(`Login failed: ${message}`);
      return null;
    }
  } catch (error) {
    console.error("Login error:", error.message);
    return null;
  }
};

export const logoutUser = async () => {
  try {
    const token = TokenService.getAuthToken();
    const response = await axiosInstance.post('/logout', {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (response.data.isSuccess) {
      return true;
    } else {
      console.error('Logout failed:', response.data.message);
      return false;
    }
  } catch (error) {
    console.error('Error during logout:', error.message);
    return false;
  }
};

const getBrokerAccounts = async (page, pageSize) => {
  try {
    const token = TokenService.getAuthToken();
    const response = await axiosInstance.get(
      `/brokerAccounts?page=${page}&pageSize=${pageSize}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response && response.data && Array.isArray(response.data)) {
      const data = response.data;
      // Map and modify the key names
      const modifiedData = data.map((account) => ({
        Broker: account.brokerName,
        Status: account.status,
        Purpose: account.loanPurpose,
        Mortgage: account.accountID,
        BorrowerName: account.primaryClient,
        "App Date": dayjs(account.applicationDate).format("YYYY-MM-DD"),
        "Closing Date": dayjs(account.closingDate).format("YYYY-MM-DD"),
        Amount: formatter.format(account.totalAmount),
        Conditions: account.numberOfOutstandingConditions
      }));
      // sort by closing date asending
      modifiedData.sort((a, b) => {
        return dayjs(a["Closing Date"]) - dayjs(b["Closing Date"]);
      });
      return modifiedData;
    } else {
      console.error("Invalid data format received from the server.");
      return null;
    }
  } catch (error) {
    // Handle error cases
    console.error(
      "Error fetching client broker agent account summaries:",
      error.message
    );
    return null;
  }
};

const getBrokerPipelineAccounts = async (page, pageSize) => {
  try {
    const token = TokenService.getAuthToken();
    const response = await axiosInstance.get(
      `/brokerPipelineAccounts?page=${page}&pageSize=${pageSize}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const modifiedData = response.data.map((account) => ({
      Broker: account.brokerName,
      Status: account.status,
      Purpose: account.loanPurpose,
      Mortgage: account.accountID,
      BorrowerName: account.primaryClient,
      "App Date": dayjs(account.applicationDate).format("YYYY-MM-DD"),
      "Closing Date": dayjs(account.closingDate).format("YYYY-MM-DD"),
      Amount: formatter.format(account.totalAmount),
      Conditions: account.numberOfOutstandingConditions
    }));

    // sort by closing date asending
    modifiedData.sort((a, b) => {
      return dayjs(a["Closing Date"]) - dayjs(b["Closing Date"]);
    });

    return modifiedData;
  } catch (error) {
    // Handle error cases
    console.error(
      "Error fetching client broker agent account summaries:",
      error.message
    );
    return null;
  }
};

const getFullAccountByAccountId = async (accoundID) => {
  try {
    const token = TokenService.getAuthToken();
    const response = await axiosInstance.get(`/dealDetails/${accoundID}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error(
      "Error fetching deal details:",
      error.message
    );
    return null;
  }
};

const getAccountConditionsStatement = async (accountID) => {
  try {
    const token = TokenService.getAuthToken();
    const response = await axiosInstance.get(
      `/accountConditionsStatement/${accountID}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error fetching account conditions statement:",
      error.message
    );
    return null;
  }
};

const getConditionTrackingByAccoundId = async (accountID) => {
  try {
    const token = TokenService.getAuthToken();
    const response = await axiosInstance.get(
      `/accountConditions/${accountID}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error fetching client broker agent account conditions by ID:",
      error.message
    );
    return null;
  }
};

const getDocumentsByAccoundId = async (accountID) => {
  try {
    const token = TokenService.getAuthToken();
    const response = await axiosInstance.get(`/accountDocuments/${accountID}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    // Handle error cases
    console.error(
      "Error fetching client broker agent account summaries:",
      error.message
    );
    return null;
  }
};

const getInternalLoanContactsByAccoundId = async (accountID) => {
  try {
    const token = TokenService.getAuthToken();
    const response = await axiosInstance.get(
      `/internalLoanContacts/${accountID}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    // Handle error cases
    console.error(
      "Error fetching client broker agent account internal contacts:",
      error.message
    );
    return null;
  }
};

const getDocumentTypes = async () => {
  try {
    const token = TokenService.getAuthToken();
    const response = await axiosInstance.get(`/documentTypes`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    // Handle error cases
    console.error(
      "Error fetching client broker agent document types:",
      error.message
    );
    return null;
  }
};

export const uploadDocuments = async (document) => {
  const token = TokenService.getAuthToken();
  try {
    const response = await axiosInstance.post('/attachFileToAccount', {
      ...document,
      fileSize: document.fileSize // Ensure this is included
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading document: ", error.response?.data || error.message);
    return error.response?.data || { isSuccess: false, message: error.message };
  }
};

const forgotPassword = async (username, options = {}) => {
  try {
    const response = await axiosInstance.post('/forgotPassword', { 
      username,
      preferredLanguage: options.preferredLanguage || 'en'
    }, {
      headers: {
        "X-TenantId": window.TENANT_ID,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error in forgot password request:", error);
    if (error.response && error.response.data) {
      return error.response.data;
    }
    return { isSuccess: false, message: "An unexpected error occurred" };
  }
};

const changePassword = async (userDetails) => {
  try {
    const response = await axiosInstance.post('/resetPassword', userDetails);

    if (response.data) {
      return {
        isSuccess: response.data.isSuccess,
        message: response.data.message
      };
    }
    
    return {
      isSuccess: false,
      message: 'No response data received'
    };

  } catch (error) {
    console.error("Error in change password request:", error);
    
    return {
      isSuccess: false,
      message: error.response?.data?.message || 'An error occurred during password change'
    };
  }
};

const getDocumentData = async (accountId, docId) => {
  try {
    const token = TokenService.getAuthToken();
    const response = await axiosInstance.get(
      `/documentData/${accountId}/${docId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    // Handle error cases
    console.error(
      "Error fetching client broker agent documents data:",
      error.message
    );
    return null;
  }
};

 const getCommitmentsClosingWithOutstandingConditions = async (page = 0, pageSize = 10) => {
  try {
    const response = await axiosInstance.get(`/brokerAgentCommitmentsClosingSoonDeals?page=${page}&pageSize=${pageSize}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching commitments closing with outstanding conditions:", error.message);
    if (error.response?.status === 401) {
      TokenService.clearAll();
      window.location.href = '/';
    }
    return null;
  }
};

 const getAllUpcomingClosing = async (page = 0, pageSize = 10) => {
  try {
    const response = await axiosInstance.get(`/brokerAgentAllUpcomingClosingDeals?page=${page}&pageSize=${pageSize}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching all upcoming closing deals:", error.message);
    if (error.response?.status === 401) {
      TokenService.clearAll();
      window.location.href = '/';
    }
    return null;
  }
};

const getPendingReview = async (page = 0, pageSize = 10) => {
  try {
    const token = TokenService.getAuthToken();
    const response = await axiosInstance.get(`/brokerAgentPendingReviewDeals?page=${page}&pageSize=${pageSize}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching pending review deals:", error.message);
    return null;
  }
};

const getYTDFundedDeals = async (page = 0, pageSize = 10) => {
  try {
    const token = TokenService.getAuthToken();
    const response = await axiosInstance.get(`/brokerAgentYearToDateFundedDeals?page=${page}&pageSize=${pageSize}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching YTD funded deals:", error.message);
    return null;
  }
};

const searchDeals = async (searchParams) => {
  try {
    const token = TokenService.getAuthToken();
    const response = await axiosInstance.post("/searchDeal", searchParams, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error searching deals:", error.message);
    return null;
  }
};

const resetPassword = async (username, newPassword, confirmPassword, token, options) => {
  try {
    const response = await axiosInstance.post('/resetPassword', {
      username,
      newPassword,
      confirmPassword,
      token,
      // eslint-disable-next-line no-undef
      preferredLanguage: options.preferredLanguage || 'en',
      // eslint-disable-next-line no-undef
      sendResetEmail: options.sendResetEmail || false,
      // eslint-disable-next-line no-undef
      sendChangePassword: options.sendChangePassword || false
    });
    return response.data;
  } catch (error) {
    console.error("Error in reset password request:", error);
    if (error.response && error.response.data) {
      return error.response.data;
    }
    return { isSuccess: false, message: "An unexpected error occurred" };
  }
};

// Admin authentication for verification process
const ADMIN_CREDENTIALS = {
  username: window.ADMIN_USERNAME,
  password: window.ADMIN_PASSWORD
};

let adminToken = null;

// Admin login for verification operations
const adminLogin = async () => {
  try {
    const response = await axiosInstance.post("/login", ADMIN_CREDENTIALS);
    
    const { isSuccess, result } = response.data;
    
    if (isSuccess && result.token) {
      adminToken = result.token;
      return adminToken;
    } else {
      throw new Error("Admin login failed");
    }
  } catch (error) {
    console.error("Admin login error:", error.message);
    throw error;
  }
};

// Admin logout for cleanup
const adminLogout = async () => {
  try {
    if (adminToken) {
      await axiosInstance.post('/logout', {}, {
        headers: {
          Authorization: `Bearer ${adminToken}`
        }
      });
    }
  } catch (error) {
    console.error('Admin logout error:', error.message);
  } finally {
    adminToken = null;
  }
};

// Get admin headers
const getAdminHeaders = () => {
  if (!adminToken) {
    throw new Error("Admin not authenticated");
  }
  return {
    Authorization: `Bearer ${adminToken}`,
    'Content-Type': 'application/json'
  };
};

// 2. POST /api/v1/verifyBrokerAgent - Verify broker agent information (WITH admin auth)
const verifyBrokerAgent = async (brokerData) => {
  try {
    // Ensure admin is logged in
    if (!adminToken) {
      await adminLogin();
    }

    const requestData = {
      firstName: brokerData.firstName,
      lastName: brokerData.lastName,
      email: brokerData.email
      // No brokerHouseContactId needed since we removed brokerage selection
    };

    const response = await axiosInstance.post('/verifyBrokerAgent', requestData, {
      headers: getAdminHeaders()
    });

    console.log("Verify Broker Agent Response:", response.data);

    return {
      isSuccess: true,
      data: response.data,
      message: response.data.message || "Verification successful"
    };
  } catch (error) {
    console.error("Error verifying broker agent:", error.message);
    
    // Handle different error scenarios
    const errorResponse = error.response?.data;
    let errorType = 'error';
    
    if (error.response?.status === 404) {
      errorType = 'not_found';
    } else if (error.response?.status === 409) {
      errorType = 'already_registered';
    }

    return {
      isSuccess: false,
      message: errorResponse?.message || error.message,
      errorType: errorType
    };
  }
};

// 3. POST /api/v1/register - Register new broker agent (WITHOUT admin auth)
const registerBrokerAgent = async (registrationData) => {
  try {

    if (!adminToken) {
      await adminLogin();
    }

    const requestData = {
      firstName: registrationData.firstName,
      lastName: registrationData.lastName,
      email: registrationData.email,
      uniqueId: "", // Always empty string as specified
      contactId: registrationData.brokerAgentContactId || "", // Use verified contact ID if available
      password: registrationData.password,
      role: "BrokerAgent",
      selectedLanguage: registrationData.selectedLanguage || "en"
    };

    const response = await axiosInstance.post('/register', requestData, {
      headers: getAdminHeaders()
    });

    await adminLogout();

    return {
      isSuccess: true,
      data: response.data,
      message: response.data.message || "Registration successful"
    };
  } catch (error) {
    console.error("Error registering broker agent:", error.message);

    await adminLogout();

    return {
      isSuccess: false,
      message: error.response?.data?.message || error.message
    };
  }
};

// Cleanup admin session
const cleanupAdminSession = async () => {
  await adminLogout();
};

export {
  axiosInstance,
  setAuthToken,
  login,
  getFullAccountByAccountId,
  getBrokerAccounts,
  getBrokerPipelineAccounts,
  changePassword,
  getConditionTrackingByAccoundId,
  getInternalLoanContactsByAccoundId,
  getDocumentsByAccoundId,
  getDocumentTypes,
  forgotPassword,
  resetPassword,
  getDocumentData,
  getCommitmentsClosingWithOutstandingConditions,
  getAllUpcomingClosing,
  getPendingReview,
  getYTDFundedDeals,
  searchDeals,
  getAccountConditionsStatement,
  verifyBrokerAgent,
  registerBrokerAgent,
  cleanupAdminSession      
};
